import { VideoJsPlayer } from "video.js";
import { VideoPreloader } from "../interfaces/VideoPreloader";
import { SingleQualityVideoJsPreloader } from "./SingleQualityVideoJsPreloader";
import { StreamData } from "../interfaces/StreamData";
import { getPlayableBufferDuration } from "../utils/buffering-detector";
import { QualityLevel } from "../../../model/qualityLevel/QualityLevel";
import { prebufferingSupported, getCurrentQualityLevel } from "../../../utils/videoJsUtils";
import { asArray } from "../utils/helper";
import { ILogger } from "js-logger";
import { obtainInstanceLogger } from "../logger";

export interface PlayerAwareVideoJsPreloaderOptions {
  minimumBufferTime?: number;
  minimumTimeToEnd?: number;
  maxSimultaneousConnections?: number;
}

export class PlayerAwareVideoJsPreloader implements VideoPreloader {
  private logger: ILogger = obtainInstanceLogger("PlayerAwareVideoJsPreloader");

  private readonly preloaders = new Map<string, SingleQualityVideoJsPreloader>();
  private readonly queued = new Array<StreamData>();
  private readonly options: PlayerAwareVideoJsPreloaderOptions;

  private currentQuality?: QualityLevel;
  private isPreloadingOn: boolean;
  private videoElement: HTMLVideoElement;

  constructor(
    private readonly player: VideoJsPlayer,
    options: PlayerAwareVideoJsPreloaderOptions = {},
  ) {
    const createLoop = () => {
      setInterval(this.doUpdate.bind(this), 1000);
      player.off("play", createLoop);
    };

    player.on("play", createLoop);

    this.options = {
      minimumBufferTime: 10,
      minimumTimeToEnd: 60,
      maxSimultaneousConnections: 4,
      ...options,
    };
  }

  cancel(sources: StreamData | StreamData[]): void {
    this.eachPreloader((preloader) => preloader.cancel(sources));
  }

  cancelAll() {
    this.eachPreloader((preloader) => preloader.cancelAll());
  }

  pause() {
    this.isPreloadingOn = false;
  }

  queue(sources: StreamData | StreamData[]): void {
    this.queued.push(...asArray(sources));

    this.eachPreloader((preloader) => preloader.queue(sources));
  }

  start() {
    this.isPreloadingOn = true;
  }

  private doUpdate() {
    if (prebufferingSupported(this.player)) {
      this.videoElement = this.player.el().querySelector("video");

      let canPreload = this.isPreloadingOn;

      canPreload = canPreload && this.isBufferLongEnough();
      canPreload = canPreload && this.isEndOfCurrentVideoIsNearEnough();

      const quality = getCurrentQualityLevel(this.player);

      if (canPreload && this.currentQuality?.id !== quality.id) {
        if (this.currentQuality !== quality) {
          this.preloaders.get(this.currentQuality?.id)?.pause();
          this.currentQuality = quality;
        }

        this.logger.log("Starting preloader with " + this.currentQuality.dimension + " quality");

        this.startPreloader(quality);
      } else if (!canPreload && this.currentQuality != null) {
        this.logger.log("Pausing preloading");

        this.eachPreloader((preloader) => preloader.pause());

        this.currentQuality = null;
      }
    }
  }

  private isBufferLongEnough(): boolean {
    return getPlayableBufferDuration(this.videoElement) >= this.options.minimumBufferTime;
  }

  private isEndOfCurrentVideoIsNearEnough(): boolean {
    return this.videoElement.duration - this.videoElement.currentTime <= this.options.minimumTimeToEnd;
  }

  private startPreloader(quality: QualityLevel) {
    if (!this.preloaders.has(quality.id)) {
      const preloader = this.initializePreloader(quality);

      this.preloaders.set(quality.id, preloader);
    }

    this.preloaders.get(quality.id).start();
  }

  private initializePreloader(quality: QualityLevel) {
    const preloader = new SingleQualityVideoJsPreloader(quality, this.options.maxSimultaneousConnections);

    preloader.queue(this.queued);

    return preloader;
  }

  private eachPreloader(callback: (preloader: SingleQualityVideoJsPreloader) => void) {
    for (const preloader of Object.values(this.preloaders)) {
      callback(preloader);
    }
  }

  /**
   * Returns elligble stream data for player,
   * so we can play HLS on Mac and MPEG-DASH on other devices
   */
  // private getEligibleForPlayer(data: StreamData[]): StreamData[] {
  //   return data.map(({ sources }) => {
  //     return sources.find(({ type }) => this.videoElement.canPlayType(type));
  //   });
  // }
}
